import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import merchantApi from "../../../lib/merchantApi";
import { useMerchantApplication } from "../../../context/MerchantApplicationContext";
import StatusPill from "../components/StatusPill";
import StepNav from "./StepNav";
import GenericStep from "./GenericStep";
import Step6Documents from "./Step6Documents";
import Step7Review from "./Step7Review";
import StepDiagnostic from "./StepDiagnostic";
import useAutoSave from "./useAutoSave";

/**
 * Onboarding orchestrator.
 *
 * Resumability:
 * - Route /app/onboarding (no step) redirects to /app/onboarding/step/{last_step from server}.
 * - Route /app/onboarding/step/:n renders that step.
 *
 * Server-driven completion:
 * - Schemas come from the server (single source of truth).
 * - Every save returns the recomputed step_status map.
 */
export default function OnboardingPage() {
  const { stepNum: rawStep } = useParams();
  const navigate = useNavigate();
  const { refresh: refreshApp } = useMerchantApplication();

  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [localStepData, setLocalStepData] = useState({});
  // Hold the optimistic local copy of the current step so typing feels
  // instant even though the server is the truth.

  // Bootstrap — fetch the full application + schemas.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await merchantApi.get("/merchant/application/full");
        if (cancelled) return;
        setApp(data);
        setLocalStepData(data.step_data || {});
        // Defensive routing: if no/invalid step in URL, redirect to last_step.
        const parsed = parseInt(rawStep || "", 10);
        const isValid = !!rawStep && Number.isInteger(parsed) && parsed >= 1 && parsed <= 7
          && /^\d+$/.test(rawStep);
        if (!isValid) {
          navigate(`/app/onboarding/step/${data.last_step || 1}`, { replace: true });
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stepNum = Math.max(1, Math.min(7, parseInt(rawStep || "1", 10) || 1));

  const onSaveMeta = useCallback((meta) => {
    // STRICT RULE: autosave / save responses NEVER write to user-editable
    // field state. We merge only server-owned metadata here. `step_data` is
    // intentionally omitted from `meta` by the autosave hook and by the
    // onAppPatch callsites — see CONTRIBUTING.md.
    //
    // Defence in depth (Bug 1 fix, Feb 2026): drop `undefined` values BEFORE
    // spreading so a caller can't accidentally clobber a previously-loaded
    // server-owned field (e.g. `schemas`) by passing it as undefined.
    const cleaned = {};
    if (meta && typeof meta === "object") {
      for (const [k, v] of Object.entries(meta)) {
        if (v !== undefined) cleaned[k] = v;
      }
    }
    setApp((prev) => ({
      ...(prev || {}),
      ...cleaned,
      step_data: prev?.step_data || {},
      step_status: cleaned.step_status || (prev?.step_status || {}),
    }));
  }, []);

  const { savingState, queueSave, flushNow } = useAutoSave(stepNum, onSaveMeta);

  // Build a per-step "patch handler" the GenericStep component will call.
  const stepKey = `step${stepNum}`;
  const currentStepData = localStepData?.[stepKey] || {};
  const onPatch = useCallback((patch) => {
    setLocalStepData((prev) => ({
      ...prev,
      [stepKey]: { ...(prev?.[stepKey] || {}), ...patch },
    }));
    queueSave(patch);
  }, [stepKey, queueSave]);

  const stepStatus = app?.step_status || {};
  const submitGated = !["1","2","3","4","5"].every((k) => stepStatus[k] === "complete");

  const stepsMeta = useMemo(
    () => Array.from({ length: 7 }, (_, i) => ({
      num: i + 1,
      title: app?.schemas?.[i + 1]?.title || `Step ${i + 1}`,
    })),
    [app?.schemas]
  );

  const gotoStep = useCallback(async (n) => {
    await flushNow();
    navigate(`/app/onboarding/step/${n}`);
  }, [flushNow, navigate]);

  if (loading) {
    return (
      <div data-testid="onboarding-loading" className="flex items-center gap-2 text-zinc-500 text-[12px] uppercase tracking-[0.22em]">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading onboarding…
      </div>
    );
  }

  if (error || !app) {
    return (
      <div data-testid="onboarding-error" className="rounded-2xl border border-rose-400/20 bg-rose-400/[0.05] p-6 text-rose-200 text-[13.5px]">
        <AlertTriangle className="h-4 w-4 inline-block mr-1.5" />
        Could not load onboarding. Please refresh.
      </div>
    );
  }

  const schemaForStep = app.schemas?.[stepNum];

  return (
    <div data-testid="onboarding-page" className="max-w-6xl">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Onboarding</div>
          <h2 className="font-display mt-2 text-3xl sm:text-4xl font-semibold tracking-tighter chrome-text leading-tight">
            {schemaForStep?.title || `Step ${stepNum}`}
          </h2>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <StatusPill status={app.status} />
          <div className="ref-mono text-[11px] text-zinc-500">{app.ref}</div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6 lg:gap-10">
        {/* Step navigator */}
        <aside data-testid="onboarding-nav-aside" className="md:sticky md:top-[5.5rem] md:self-start">
          <StepNav
            steps={stepsMeta}
            current={stepNum}
            stepStatus={stepStatus}
            submitGated={submitGated}
            onSelect={gotoStep}
          />
        </aside>

        {/* Step content */}
        <section data-testid={`onboarding-step-${stepNum}`} className="min-w-0">
          {/* Inline saving indicator */}
          <div className="mb-6 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">
              Step {stepNum} of 7 · <span className={statusColor(stepStatus[String(stepNum)])}>
                {stepStatus[String(stepNum)] || "incomplete"}
              </span>
            </div>
            <SavingIndicator state={savingState} />
          </div>

          {stepNum === 6 ? (
            <Step6Documents
              app={app}
              onAppPatch={onSaveMeta}
              submitGated={submitGated}
              stepStatus={stepStatus}
              gotoStep={gotoStep}
            />
          ) : stepNum === 7 ? (
            <Step7Review
              app={app}
              schemas={app.schemas}
              submitGated={submitGated}
              gotoStep={gotoStep}
              onSubmitted={(fresh) => {
                // Step 7 submit transitions the application to a non-editable
                // (submitted) state. At that point fields are read-only on
                // the server side, so it's safe to replace the full app
                // snapshot. If we ever re-enable editing on submitted apps,
                // this needs to go back through `onSaveMeta` only.
                setApp(fresh);
                refreshApp();
              }}
            />
          ) : (
            <GenericStep
              schema={schemaForStep}
              value={currentStepData}
              onPatch={onPatch}
              flushNow={flushNow}
              testIdPrefix={`step${stepNum}`}
            />
          )}

          {/* "Why is this incomplete?" diagnostic — surfaces the
             validator's silent-drop reasons so merchants can self-heal
             instead of re-typing every field. Skip step 7 (review-only). */}
          {stepNum !== 7 && (
            <StepDiagnostic stepNum={stepNum} status={stepStatus[String(stepNum)] || "incomplete"} />
          )}

          {/* Footer nav */}
          <div className="mt-10 flex items-center justify-between">
            <button
              type="button"
              onClick={() => gotoStep(Math.max(1, stepNum - 1))}
              disabled={stepNum === 1}
              data-testid="onboarding-back-btn"
              className="group inline-flex items-center gap-1.5 rounded-full border border-white/[0.10] bg-white/[0.02] hover:bg-white/[0.06] disabled:opacity-30 disabled:cursor-not-allowed text-white px-4 py-2 text-[13px] font-medium transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
              Back
            </button>
            <button
              type="button"
              onClick={() => {
                const next = stepNum + 1;
                if (next === 7 && submitGated) return;
                if (next > 7) return;
                gotoStep(next);
              }}
              disabled={stepNum === 7 || (stepNum === 6 && submitGated)}
              data-testid="onboarding-continue-btn"
              className="group inline-flex items-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all px-5 py-2 text-[13px] font-medium"
            >
              Continue
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

function statusColor(s) {
  return s === "complete" ? "text-emerald-300" : s === "partial" ? "text-amber-300" : "text-zinc-400";
}

function SavingIndicator({ state }) {
  if (state === "saving") {
    return (
      <div data-testid="onboarding-saving" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-zinc-500">
        <Loader2 className="h-3 w-3 animate-spin" /> Saving…
      </div>
    );
  }
  if (state === "saved") {
    return (
      <div data-testid="onboarding-saved" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-emerald-300">
        <CheckCircle2 className="h-3 w-3" /> Saved
      </div>
    );
  }
  return <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-600">Auto-save on</div>;
}
